<template>
  <div class="common-table">
    <slot name="header"></slot>
    <a-table
      ref="tableRef"
      :columns="tableColumns"
      :dataSource="tableData"
      :loading="getLoading"
      :pagination="
        showPagination && fetchData
          ? {
              current: pagination.pageNo,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条数据`,
              pageSizeOptions: ['10', '20', '30', '40', '50', '100'],
              onChange: handlePageChange,
              onShowSizeChange: handleSizeChange,
            }
          : false
      "
      :rowKey="rowKey"
      :scroll="scroll"
      v-bind="$attrs"
      v-on="$listeners"
    >
      <template v-for="(_, slot) in $scopedSlots" v-slot:[slot]="text, record, index">
        <slot :name="slot" :text="text" :record="record" :index="index" />
      </template>
      <template v-if="!$scopedSlots.index" v-slot:index="text, record, index">
        {{ getIndex(index) }}
      </template>
    </a-table>
  </div>
</template>

<script>
  export default {
    name: 'MpmTable',
    inheritAttrs: false,
    props: {
      // 表格列配置
      columns: {
        type: Array,
        required: true,
      },
      // 获取数据的Promise方法
      fetchData: {
        type: Function,
        default: null,
      },
      // 父组件传入的数据源
      data: {
        type: Array,
        default: () => [],
      },
      // 是否显示分页
      showPagination: {
        type: Boolean,
        default: true,
      },
      // 行数据的 Key
      rowKey: {
        type: [String, Function],
        default: '',
      },
      // 表格滚动配置
      scroll: {
        type: Object,
        default: () => ({ x: 1000, y: 700 }),
      },
      // 加载状态
      loading: {
        type: Boolean,
        default: false,
      },
      // 初始分页配置
      initialPagination: {
        type: Object,
        default: () => ({
          pageNo: 1,
          pageSize: 20,
        }),
      },
      // 是否自动执行表格接口
      autoQuery: {
        type: Boolean,
        default: true,
      },
      // 额外的请求参数
      extraParams: {
        type: Object,
        default: () => ({}),
      },
      // 格式化数据接口返回的数据
      responseHandler: {
        type: Function,
        default: (res) => {
          return res
        },
      },
    },
    computed: {
      tableColumns() {
        return this.columns.map((col) => {
          const newCol = { ...col }
          // 处理表头slots
          if (col.slots?.title) {
            newCol.title = (h) => {
              return this.$slots[col.slots.title]
            }
          }

          // 如果列没有自定义渲染且不是操作列，添加默认的空值处理
          if (!newCol.scopedSlots?.customRender && !newCol.slots?.customRender && !newCol.customRender) {
            newCol.customRender = (value, row) => {
              // 处理format函数
              let displayValue = value
              if (newCol.format && typeof newCol.format === 'function') {
                displayValue = newCol.format(value, row)
              }
              // 处理空值
              displayValue = this.formatEmptyValue(displayValue)

              return newCol.ellipsis ? (
                <a-tooltip placement="topLeft" title={displayValue}>
                  <span>{displayValue}</span>
                </a-tooltip>
              ) : (
                displayValue
              )
            }
          }

          return newCol
        })
      },
      // 计算loading状态
      getLoading() {
        return this.fetchData ? this.innerLoading : this.loading
      },
      // 表格数据源
      tableData() {
        // 如果是父组件传入数据模式
        if (!this.fetchData) {
          return this.data || []
        }
        // 如果是自动获取数据模式
        return this.dataSource
      },
    },
    data() {
      return {
        innerLoading: false,
        dataSource: [],
        pagination: {
          pageNo: this.initialPagination.pageNo,
          pageSize: this.initialPagination.pageSize,
          total: 0,
        },
      }
    },
    watch: {
      // 监听父组件传入的数据变化
      data: {
        handler(newVal) {
          if (!this.fetchData) {
            // 更新分页总数
            this.pagination.total = newVal.length
          }
        },
        immediate: true,
      },
    },
    created() {
      if (this.fetchData && this.autoQuery) {
        this.loadData()
      }
    },
    methods: {
      getTableRef() {
        return this.$refs.tableRef
      },
      // 格式化空值
      formatEmptyValue(value) {
        if (value === undefined || value === null || value === '') {
          return '-'
        }
        return value
      },
      // 计算序号
      getIndex(index) {
        const { pageNo, pageSize } = this.pagination
        return (pageNo - 1) * pageSize + index + 1
      },
      async loadData() {
        if (!this.fetchData) return

        try {
          this.innerLoading = true
          const { pageNo, pageSize } = this.pagination
          const params = {
            pageNo,
            pageSize,
            ...this.extraParams,
          }

          const res = await this.fetchData({ data: params })
          const list = res.list || res.records || []
          let rows = this.responseHandler ? this.responseHandler(list) : list
          // 返回数据格式为 { records: [], total: number }
          this.dataSource = rows || []
          this.pagination = {
            ...this.pagination,
            total: Number(res.total || 0),
          }

          // 触发数据加载完成事件
          this.$emit('load-success', {
            data: this.dataSource,
            pagination: this.pagination,
          })
        } catch (error) {
          console.error('加载数据失败:', error)
          this.$message.error('加载数据失败')
          // 触发数据加载失败事件
          this.$emit('load-error', error)
        } finally {
          this.innerLoading = false
        }
      },
      // 处理页码改变
      handlePageChange(page) {
        this.pagination.pageNo = page
        this.loadData()
      },
      // 处理每页条数改变
      handleSizeChange(pageNo, size) {
        this.pagination.pageNo = 1
        this.pagination.pageSize = size
        this.loadData()
      },
      // 搜索查询表格
      query() {
        this.pagination.pageNo = 1
        this.$nextTick(() => {
          this.loadData()
        })
      },
      // 刷新表格数据
      refresh() {
        this.loadData()
      },
      // 重置分页
      reset() {
        this.pagination = {
          pageNo: this.initialPagination.pageNo,
          pageSize: this.initialPagination.pageSize,
          total: 0,
        }
        this.loadData()
      },
    },
  }
</script>

<style lang="scss" scoped>
  .common-table {
    background: #fff;
    border-radius: 2px;
  }

  /* 让所有表格单元格垂直居中 */
  .ant-table-tbody > tr > td {
    vertical-align: middle !important;
  }
</style>
